import Action from '../../actions/userAccount'
import { default as NotificationAction } from '../../actions/notification'
import { ILicense } from '../../models/License'
import { IUser } from '../../models/User'
import {
  AuthorizationActions,
  LimitActions,
  NotificationActions,
  UserAccountActions,
} from './actionNames'
import initialStore from './initialState'
import authorize from './mutators/authorizationGranted'
import licenseDataAccepted from './mutators/licenseDataAccepted'
import requestAuth from './mutators/requestAuthorization'
import requestUserData from './mutators/requestUserData'
import signoffSession from './mutators/signoffSession'
import switchLanguage from './mutators/switchLanguage'
import userDataAccepted from './mutators/userDataAccepted'
import userDataRejected from './mutators/userDataRejected'
import toggleLimitModal from './mutators/toggleLimitModal'
import limitAccepted from './mutators/limitAccepted'
import toggleInfoModal from './mutators/toggleInfoModal'
import setSliderTab from './mutators/setSliderTab'
import notificationsAccepted from './mutators/notifications/notificationsAccepted'
import markAsRead from './mutators/notifications/markAsRead'
import markAsSeen from './mutators/notifications/markAsSeen'
import deleteNotification from './mutators/notifications/deleteNotification'
import reloadNotifications from './mutators/notifications/reloadNotifications'
import syncNotifications from './mutators/notifications/syncNotifications'
import sendError from './mutators/notifications/sendError'
import errorSent from './mutators/notifications/errorSent'
import loadNotification from './mutators/notifications/loadNotification'
import { IntegrationAction } from '../airbnb/actionNames'
import { CalendarActions } from '../bookingCalendar/actionNames'
import { isEmpty } from 'lodash'

export default function(
  store: Readonly<IUserAccountStore> = initialStore,
  action: Action | NotificationAction | any,
): IUserAccountStore {
  switch (action.type) {
    case AuthorizationActions.RequestAuthorization:
      return requestAuth(store, action)
    case AuthorizationActions.AuthorizationGranted:
      return authorize(store, action)
    case AuthorizationActions.AuthorizationRejected:
      return signoffSession(store)
    case AuthorizationActions.SignOffTheAccount:
      return signoffSession(store)

    case UserAccountActions.RequestUserData:
      return requestUserData(store)
    case 'FETCH/Fetch user from API':
      return requestUserData(store)

    case UserAccountActions.UserDataAccepted:
      return userDataAccepted(store, action)
    case UserAccountActions.SaveUser:
      return userDataAccepted(store, action)
    case UserAccountActions.UserDataRejected:
      return userDataRejected(store, action)

    case UserAccountActions.LicenseDataAccepted:
      return licenseDataAccepted(store, action)
    case UserAccountActions.SubCancelled:
      return {
        ...store,
        license: {
          ...store.license,
          items: store.license.items.map(l => {
            if(isEmpty(l.sub)) return l
            if(l.sub.id_order === action.id) {
              return {
                ...l,
                sub: undefined
              }
            }
            return l
          }),
        }
      }
    case UserAccountActions.InvoiceLicenseDataAccepted:
      return {
        ...store,
        license: {
          isLoading: false,
          items: [...store.license.items, action.license],
        },
      }

    case UserAccountActions.LanguageSwitched:
      return switchLanguage(store, action)
    case AuthorizationActions.FlushState:
      return {
        ...initialStore,
        user: { ...initialStore.user, savedLanguage: store.user.savedLanguage },
      }

    case LimitActions.ToggleLimitModal:
      return toggleLimitModal(store, action)
    case LimitActions.LimitsWasAccepted:
      return limitAccepted(store, action)

    case UserAccountActions.ToggleInfoModal:
      return toggleInfoModal(store, action)

    case NotificationActions.PageAccepted:
      return notificationsAccepted(store, action)
    case LimitActions.SetSliderTab:
      return setSliderTab(store, action)
    case NotificationActions.MarkAsRead:
      return markAsRead(store, action)
    case NotificationActions.MarkAsSeenAll:
      return markAsSeen(store)
    case NotificationActions.Delete:
      return deleteNotification(store, action)
    case NotificationActions.Reload:
      return reloadNotifications(store, action)
    case NotificationActions.SyncAccepted:
      return syncNotifications(store, action)
    case NotificationActions.SendError:
      return sendError(store, action)
    case NotificationActions.ErrorSended:
      return errorSent(store, action)
    case NotificationActions.LoadPage:
      return loadNotification(store)
    case UserAccountActions.ToggleDeleteError:
      return {
        ...store,
        deleteError: action.payload,
      }
    case IntegrationAction.SetPropertyIdRedux:
      return {
        ...store,
        user: {
          ...store.user,
          booking_property_id: action.payload,
        },
      }
    case IntegrationAction.SetBookingStatusRedux:
      return {
        ...store,
        user: {
          ...store.user,
          booking_integration_status: action.payload,
        },
      }
    case CalendarActions.ToggleSurveyModal:
      return {
        ...store,
        user: {
          ...store.user,
          poll_displayed: true
        }
      }
    case UserAccountActions.OpenCookies:
      return {
        ...store,
        cookies: {
          open: true,
          forceSave: action.force? true : false,
          step: action.step? action.step : 0,
        },
      }
    case UserAccountActions.CloseCookies:
      return {
        ...store,
        cookies: {
          open: false,
          forceSave: false,
          step: 0,
        },
      }
    case UserAccountActions.SetUserWorkerId:
      return {
        ...store,
        user: {
          ...store.user,
          id_worker: action.payload,
        },
      }
    case 'dev function':
      return dev(store)
  }

  return store
}

function dev(store: IUserAccountStore) {
  return {
    ...store,
    notifications: {
      ...store.notifications,
      items: [
        ...store.notifications.items.map(not => {
          not.readAt = undefined
          return not
        }),
      ],
    },
  }
}

export interface IUserAccountStore {
  credentials: {
    username?: string
    accessToken?: string
  }
  cookies: {
    open: boolean
    forceSave: boolean
    step: 0 | 1
  }
  license: {
    isLoading: boolean
    items: ILicense[]
  }
  limits: ILimits
  limitModal: {
    open: boolean
  }
  isAuthorizing: boolean
  infoModal: {
    open: boolean
  }
  notifications: {
    count: number
    countNotRead: number
    countNotSeen: number
    hasMore: boolean
    seen: boolean
    items: any[]
    isLoading: boolean
  }
  user?: IUser
  request: IRequestState
  sliderTab: number
  deleteError: boolean
}

export interface ILimits {
  addLimit: number
  editLimit: number
  copyLimit: number
  transferLimit: number
  add: number
  edit: number
  copy: number
  transfer: number
  resetDate: Date
}

interface IRequestState {
  isLoading: boolean
  errMessage: string
}
